import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  Calendar, 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Sparkles,
  Users,
  TrendingUp
} from 'lucide-react';

const CTASection = () => {
  const navigate = useNavigate();

  const contactMethods = [
    {
      icon: Phone,
      title: '24/7 Phone Support',
      description: 'Speak with our experts anytime',
      action: 'Call Now',
      value: '+91 73046 60050',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Mail,
      title: 'Email Consultation',
      description: 'Detailed responses within 2 hours',
      action: 'Send Email',
      value: 'Sales@oratorhype.com',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      description: 'Instant support from our team',
      action: 'Start Chat',
      value: 'Available Now',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <section className="relative py-16 lg:py-24 overflow-hidden bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900">
      {/* Bubble Effects - Same as testimonial */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-5 w-16 h-16 bg-white/5 backdrop-blur-sm rounded-2xl animate-float-slow"></div>
        <div className="absolute top-1/2 right-8 w-12 h-12 bg-cyan-400/10 backdrop-blur-sm rounded-2xl animate-float"></div>
        <div className="absolute bottom-16 left-1/4 w-20 h-20 bg-purple-500/10 backdrop-blur-sm rounded-2xl animate-float-slow delay-1000"></div>
        <div className="absolute bottom-10 right-10 w-16 h-16 bg-emerald-400/10 backdrop-blur-sm rounded-xl animate-float delay-2000"></div>
        <div className="absolute top-1/3 left-1/4 w-24 h-24 bg-blue-500/5 backdrop-blur-xl rounded-2xl animate-float-slow delay-1500"></div>
        <div className="absolute top-20 right-1/3 w-20 h-20 bg-pink-500/5 backdrop-blur-xl rounded-2xl animate-float delay-500"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4">
            <Sparkles className="w-3 h-3 text-cyan-400" />
            <span className="text-xs font-semibold text-white tracking-wider">GET STARTED TODAY</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Ready to Transform Your
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-white">
              Digital Presence?
            </span>
          </h2>
          
          <p className="text-gray-300 text-sm max-w-lg mx-auto">
            Join 196+ satisfied clients who have revolutionized their business with our solutions
          </p>
        </motion.div>

        {/* Main CTA Card */}
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glass Card Background */}
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-10 shadow-2xl">
              {/* Gradient glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 via-blue-400/5 to-purple-400/10 rounded-3xl blur-xl -inset-1"></div>

              <div className="relative z-10">
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => console.log('Schedule consultation')}
                    className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl shadow-xl hover:shadow-cyan-500/30 transition-all duration-300"
                  >
                    <span className="flex items-center gap-3">
                      <Calendar className="w-5 h-5" />
                      Schedule Free Consultation
                    </span>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/case-studies')}
                    className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300 flex items-center gap-3"
                  >
                    <FileText className="w-5 h-5" />
                    View Success Stories
                  </motion.button>
                </div>

                {/* Contact Methods */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {contactMethods.map((method, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className="group"
                    >
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${method.gradient} flex items-center justify-center mb-4 mx-auto`}>
                          <method.icon className="w-7 h-7 text-white" />
                        </div>
                        
                        <h3 className="text-lg font-bold text-white text-center mb-2">
                          {method.title}
                        </h3>
                        
                        <p className="text-gray-300 text-sm text-center mb-4">
                          {method.description}
                        </p>
                        
                        <div className="text-center">
                          <button className="text-cyan-300 font-medium hover:text-cyan-200 transition-colors duration-300 flex items-center gap-2 justify-center mx-auto text-sm">
                            <span>{method.action}</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                          <p className="text-gray-400 text-xs mt-2">{method.value}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { value: '196+', label: 'Clients' },
                    { value: '24/7', label: 'Support' },
                    { value: '100%', label: 'Success' }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="text-center group"
                    >
                      <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-gray-300">
                        {stat.label}
                      </div>
                      <div className="w-0 group-hover:w-12 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent transition-all duration-500 mt-1 mx-auto"></div>
                    </motion.div>
                  ))}
                </div>

                {/* Footer */}
                <div className="pt-6 border-t border-white/10">
                  <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                    {[
                      'No Credit Card Required',
                      'Free Consultation',
                      'Custom Solutions'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        
        .delay-500 { animation-delay: 0.5s; }
        .delay-1000 { animation-delay: 1s; }
        .delay-1500 { animation-delay: 1.5s; }
        .delay-2000 { animation-delay: 2s; }
      `}</style>
    </section>
  );
};

export default CTASection;